/**
 * Session in-memory store for invoice templates when the Sattva Backend
 * template APIs are not yet available (404/501/network).
 *
 * IMPORTANT: This is NOT a durable store. It never writes to localStorage.
 * Custom templates created here are lost on refresh. The backend tables/
 * endpoints in backend-handoff/INVOICE_TEMPLATES_API_DOCUMENTATION.md §5 are required for
 * real persistence across users and sessions.
 *
 * The legacy key `crm_invoice_templates_v1` is purged on module load so it
 * cannot remain a standing source of truth.
 */

export const LEGACY_TEMPLATES_STORAGE_KEY = 'crm_invoice_templates_v1'

/** @type {object[]} */
let memoryRows = []

try {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(LEGACY_TEMPLATES_STORAGE_KEY)
  }
} catch {
  /* private mode / blocked storage — ignore */
}

function readAll() {
  return memoryRows.slice()
}

function writeAll(rows) {
  memoryRows = Array.isArray(rows) ? rows.slice() : []
}

function nextId(rows) {
  const max = rows.reduce((m, r) => Math.max(m, Number(r.id) || 0), 0)
  return max + 1
}

function normalizeClientId(value) {
  if (value === undefined || value === null) return null
  const s = String(value).trim()
  return s || null
}

/**
 * Match priority (same as backend spec):
 * 1. client + category
 * 2. client only (category null)
 * 3. global (client null)
 */
export function findBestForClient(clientId, category = null) {
  const rows = readAll().filter((r) => r.status === 'active')
  const client = normalizeClientId(clientId)
  const cat = category ? String(category).trim() : null

  const score = (row) => {
    const rowClient = normalizeClientId(row.client_id || row.client_name)
    const rowCat = row.category ? String(row.category).trim() : null
    if (client && rowClient === client && rowCat === cat) return 3
    if (client && rowClient === client && !rowCat) return 2
    if (!rowClient) return 1
    return 0
  }

  const ranked = rows
    .map((r) => ({ row: r, s: score(r) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || (b.row.version || 0) - (a.row.version || 0))

  return ranked[0] ? ranked[0].row : null
}

export function listLocal(filters = {}) {
  let rows = readAll()
  if (filters.status) rows = rows.filter((r) => r.status === filters.status)
  if (filters.clientId) {
    const c = normalizeClientId(filters.clientId)
    rows = rows.filter((r) => normalizeClientId(r.client_id || r.client_name) === c)
  }
  if (!filters.includeArchived) rows = rows.filter((r) => r.status !== 'archived')
  return rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
}

export function getLocalById(id) {
  return readAll().find((r) => String(r.id) === String(id)) || null
}

export function createLocal(payload) {
  const rows = readAll()
  const now = new Date().toISOString()
  const row = {
    id: nextId(rows),
    name: payload.name,
    client_id: payload.client_id || null,
    client_name: payload.client_name || null,
    category: payload.category || null,
    status: payload.status || 'draft',
    version: 1,
    parent_id: null,
    config: payload.config || {},
    description: payload.description || null,
    createdAt: now,
    updatedAt: now,
    _source: 'session',
  }
  rows.push(row)
  writeAll(rows)
  return row
}

export function updateLocal(id, payload) {
  const rows = readAll()
  const idx = rows.findIndex((r) => String(r.id) === String(id))
  if (idx === -1) return null

  const existing = rows[idx]
  if (existing.status === 'archived') return null

  if (existing.status === 'active') {
    rows[idx] = { ...existing, status: 'archived', updatedAt: new Date().toISOString() }
    const newRow = {
      ...existing,
      id: nextId(rows),
      name: payload.name || existing.name,
      config: payload.config || existing.config,
      description: payload.description ?? existing.description,
      status: 'active',
      version: (existing.version || 1) + 1,
      parent_id: existing.id,
      updatedAt: new Date().toISOString(),
      _source: 'session',
    }
    rows.push(newRow)
    writeAll(rows)
    return newRow
  }

  rows[idx] = {
    ...existing,
    ...payload,
    id: existing.id,
    updatedAt: new Date().toISOString(),
    _source: 'session',
  }
  writeAll(rows)
  return rows[idx]
}

export function updateStatusLocal(id, status) {
  const rows = readAll()
  const idx = rows.findIndex((r) => String(r.id) === String(id))
  if (idx === -1) return null
  if (rows[idx].status === 'archived') return null
  rows[idx].status = status
  rows[idx].updatedAt = new Date().toISOString()
  writeAll(rows)
  return rows[idx]
}

export function cloneLocal(sourceId, overrides = {}) {
  const source = getLocalById(sourceId)
  if (!source) return null
  const rows = readAll()
  const now = new Date().toISOString()
  const row = {
    ...source,
    id: nextId(rows),
    name: overrides.name || `${source.name} (Copy)`,
    client_id: overrides.client_id ?? source.client_id,
    client_name: overrides.client_name ?? source.client_name,
    category: overrides.category ?? source.category,
    status: 'draft',
    version: 1,
    parent_id: source.id,
    config: JSON.parse(JSON.stringify(source.config)),
    createdAt: now,
    updatedAt: now,
    _source: 'session',
  }
  rows.push(row)
  writeAll(rows)
  return row
}

export function getVersionsLocal(id) {
  const rows = readAll()
  const current = getLocalById(id)
  if (!current) return []
  const chain = new Set([String(id)])
  rows.forEach((r) => {
    if (r.parent_id && chain.has(String(r.parent_id))) chain.add(String(r.id))
    if (String(r.id) === String(id)) chain.add(String(r.id))
  })
  return rows
    .filter((r) => chain.has(String(r.id)) || String(r.parent_id) === String(id))
    .sort((a, b) => (b.version || 0) - (a.version || 0))
}

export function removeLocal(id) {
  const rows = readAll()
  const idx = rows.findIndex((r) => String(r.id) === String(id))
  if (idx === -1) return false
  if (rows[idx].status !== 'draft') return false
  rows.splice(idx, 1)
  writeAll(rows)
  return true
}

export function isApiSuccess(res) {
  return res && (res.status === 'OK' || res.status === 201) && res.data !== undefined
}

export function shouldUseLocalFallback(err, res) {
  if (res && res.status === 'NOT_IMPLEMENTED') return true
  const code = err && err.response && err.response.status
  return !code || code === 404 || code === 501 || code >= 500
}

/** True when the last template call used session memory instead of the API. */
export function isSessionFallbackActive() {
  return memoryRows.some((r) => r._source === 'session') || memoryRows.length > 0
}
